import React, { useMemo, useId } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

const Portal = ({ children }) => createPortal(children, document.body);

export default function Combobox({
  className,
  label,
  required,
  placeholder,
  value,
  onChange,
  open,
  setOpen,
  anchorRef,
  panelRef,
  pos,
  items = [],
  filterFn,
  renderItem,
  getKey,
  onSelect,
  ariaLabel = "Opciones",
  helpText,
  rightIcon,
  isSelected,
  selectedKey,
  inputClassName = "",
}) {
  const listId = useId();

  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    if (!filterFn) return list;
    return list.filter(filterFn);
  }, [items, filterFn]);

  const listToRender = filtered.length ? filtered : items;

  const isClientes = String(className || "").includes("cf-combobox--clientes");
  const baseWidth = typeof pos?.width === "number" ? pos.width : null;

  const clientesMaxWidth = 520;

  let panelWidth = pos?.width ?? "auto";
  let panelLeft = pos?.left ?? 0;

  if (isClientes && baseWidth) {
    const w = Math.min(baseWidth, clientesMaxWidth);
    panelWidth = w;
    const delta = (baseWidth - w) / 2;
    panelLeft = (pos?.left ?? 0) + delta;
  }

  return (
    <div className="cf-field">
      {label ? (
        <label className="cf-label">
          {label} {required ? <span className="cf-required">*</span> : null}
        </label>
      ) : null}

      <div className={`cf-combobox ${className || ""}`} ref={anchorRef}>
        <div className="cf-combobox-control">
          <input
            className={`cf-input cf-combobox-input ${inputClassName}`}
            value={value || ""}
            required={Boolean(required)}
            placeholder={placeholder}
            onChange={(e) => {
              onChange?.(e.target.value);
              setOpen?.(true);
            }}
            onFocus={() => setOpen?.(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen?.(false);
            }}
            aria-controls={listId}
            aria-autocomplete="list"
          />

          <button
            type="button"
            className="cf-combobox-btn"
            onClick={() => setOpen?.((v) => !v)}
            aria-label="Abrir opciones"
            aria-expanded={Boolean(open)}
            aria-controls={listId}
          >
            {rightIcon ? (
              rightIcon
            ) : open ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
        </div>

        {open ? (
          <Portal>
            <div
              ref={panelRef}
              className={`cf-combobox-panel cf-combobox-panel--portal ${
                className || ""
              }`}
              role="listbox"
              id={listId}
              aria-label={ariaLabel}
              style={{
                position: "fixed",
                right: "auto",
                left: panelLeft,
                width: panelWidth,
                maxHeight: pos?.maxHeight ?? 350,
                overflowY: "auto",
                ...(pos?.placement === "top"
                  ? { bottom: window.innerHeight - (pos?.top ?? 0) }
                  : { top: pos?.top ?? 0 }),
              }}
              onWheel={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {listToRender.map((it) => {
                const key = getKey ? getKey(it) : String(it);
                const node = renderItem ? renderItem(it) : String(it);

                const selected =
                  typeof isSelected === "function"
                    ? Boolean(isSelected(it))
                    : selectedKey !== undefined && selectedKey !== null
                      ? String(key) === String(selectedKey)
                      : String(value || "") === String(it);

                return (
                  <div
                    key={key}
                    className={`cf-combobox-item ${
                      selected ? "is-selected" : ""
                    }`}
                    role="option"
                    aria-selected={selected}
                    tabIndex={0}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => onSelect?.(it)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSelect?.(it);
                      if (e.key === "Escape") setOpen?.(false);
                    }}
                  >
                    {node}
                  </div>
                );
              })}
            </div>
          </Portal>
        ) : null}
      </div>

      {helpText ? <div className="cf-help">{helpText}</div> : null}
    </div>
  );
}
